import { useState } from "react";
import { toast } from "sonner";
import { Heart, Volume2, VolumeX } from "lucide-react";
import { useCoinCelebration, CASHAPP_URL, CASHTAG, QR_URL } from "./coinCelebration";

const PRESETS = ["5", "10", "25", "50"];
const GOLDEN_THRESHOLD = 50;

const DonationBox = () => {
  const [amount, setAmount] = useState("10");
  const [soundOn, setSoundOn] = useState(true);
  const { celebrate, layer } = useCoinCelebration(soundOn);

  const donate = (e) => {
    const val = Math.round(parseFloat(amount) * 100) / 100;
    if (!val || val < 1) {
      e.preventDefault();
      toast.error("Enter at least $1 to donate.");
      return;
    }
    const big = val >= GOLDEN_THRESHOLD;
    celebrate(big);
    if (big) {
      toast.success(`$${val.toFixed(2)} — a golden downpour! Thank you, truly.`);
    } else {
      toast.success(`Cash App is open for $${val.toFixed(2)} — thank you for supporting the arts!`);
    }
  };

  return (
    <div className="relative mt-9 mx-auto max-w-md">
      {layer}
      <div
        data-testid="donation-box"
        className="bg-tile border border-gold/40 clip-frame p-7 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-amber/20 border border-amber/50 flex items-center justify-center">
            <Heart size={15} className="text-gold fill-gold" />
          </span>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold flex-1">
            Support the Arts
          </p>
          <button
            data-testid="coin-sound-toggle"
            onClick={() => setSoundOn(!soundOn)}
            aria-label={soundOn ? "Mute coin sound" : "Unmute coin sound"}
            className="text-cream/35 hover:text-gold transition-colors duration-300"
          >
            {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
        </div>
        <p className="font-serif italic font-light text-2xl text-cream mt-4">
          Leave a coin in the case.
        </p>
        <p className="text-xs text-cream/55 leading-relaxed mt-2">
          Donations open Cash App straight to{" "}
          <span className="text-gold font-medium">{CASHTAG}</span> — every coin lands
          directly with Dani. $50+ brings the golden downpour.
        </p>
        <div className="flex gap-2.5 mt-6">
          {PRESETS.map((p) => (
            <button
              key={p}
              data-testid={`donate-amount-${p}`}
              onClick={() => setAmount(p)}
              className={`flex-1 font-mono text-xs py-2.5 rounded-full border transition-colors duration-300 ${
                amount === p
                  ? "bg-gold text-ink border-gold"
                  : "border-cream/20 text-cream/60 hover:border-gold hover:text-gold"
              }`}
            >
              ${p}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3 border-b border-cream/20 focus-within:border-gold transition-colors duration-300">
          <span className="font-mono text-sm text-gold">$</span>
          <input
            data-testid="donate-custom-input"
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Any amount"
            aria-label="Custom donation amount"
            className="w-full bg-transparent outline-none py-3 text-cream placeholder:text-cream/30 font-mono text-sm"
          />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/35 whitespace-nowrap">
            any amount
          </span>
        </div>
        <a
          data-testid="donate-button"
          href={CASHAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={donate}
          className="block text-center mt-5 w-full bg-amber text-cream font-mono text-[11px] uppercase tracking-[0.25em] py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 cursor-pointer"
        >
          Donate via Cash App
        </a>
        <div className="mt-6 flex flex-col items-center gap-3">
          <div className="clip-frame border border-gold/40 overflow-hidden w-28">
            <img
              data-testid="donate-qr"
              src={QR_URL}
              alt="Cash App QR code — Pay $morrdanimorr"
              className="w-full h-28 object-cover"
              loading="lazy"
            />
          </div>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream/40 text-center">
            Scan to pay {CASHTAG} on Cash App
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationBox;
